import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "./shell/AppHeader";
import { BottomNavBar } from "./shell/BottomNavBar";
import { CollapsedSidebar } from "./shell/CollapsedSidebar";
import { CommandPalette, useCommandPaletteShortcut } from "./shell/CommandPalette";
import { TabBar } from "./shell/TabBar";
import { SidebarPinToggle } from "./shell/SidebarPinToggle";
import { CopilotWidget } from "./CopilotWidget";
import { PinSetupBanner } from "./PinSetupBanner";
import { SessionLockOverlay } from "./SessionLockOverlay";
import { Toasts } from "./ui";
import { useSessionIdle } from "../hooks/useSessionIdle";
import { getSocket } from "../lib/socket";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useShellTabs } from "../stores/shell-tabs";
import { useShellBottomBar } from "../stores/shell-bottom-bar";
import { settingsQueryOptions } from "../hooks/useSettingsDraft";

export function Layout() {
  const { can, moduleEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const initFromPath = useShellTabs((s) => s.initFromPath);
  const syncActiveTabFromPath = useShellTabs((s) => s.syncActiveTabFromPath);
  const copilotOpen = useShellBottomBar((s) => s.copilotOpen);
  const setCopilotOpen = useShellBottomBar((s) => s.setCopilotOpen);

  useQuery(settingsQueryOptions());

  useEffect(() => {
    initFromPath(location.pathname);
  }, [initFromPath, location.pathname]);

  useEffect(() => {
    syncActiveTabFromPath(location.pathname);
  }, [location.pathname, syncActiveTabFromPath]);

  useCommandPaletteShortcut(() => setPaletteOpen(true), true);
  useSessionIdle();

  useEffect(() => {
    const socket = getSocket();
    const onNotify = (n: { title: string }) => toast("info", n.title);
    const onNotification = (n: { title: string }) => {
      toast("info", n.title);
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    };
    const onReceipt = () => {
      void queryClient.invalidateQueries({ queryKey: ["receipts"] });
      toast("info", "New bank receipt awaiting review");
    };
    socket.on("notify", onNotify);
    socket.on("notification", onNotification);
    socket.on("bo:receipt:new", onReceipt);
    return () => {
      socket.off("notify", onNotify);
      socket.off("notification", onNotification);
      socket.off("bo:receipt:new", onReceipt);
    };
  }, [queryClient]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!event.shiftKey || event.key.toLowerCase() !== "m") return;
      if (globalThis.innerWidth <= 768) return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (event.target as HTMLElement | null)?.isContentEditable) return;
      event.preventDefault();
      navigate("/os");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className="flex h-full flex-col">
      <AppHeader onOpenCommandPalette={() => setPaletteOpen(true)} />
      <div className="flex shrink-0 border-b border-line bg-surface-2">
        <SidebarPinToggle />
        <TabBar />
      </div>

      <div className="flex min-h-0 flex-1">
        <CollapsedSidebar />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4 lg:p-6">
          <PinSetupBanner />
          <div className="flex min-h-0 flex-1 flex-col">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Must stay after <Outlet /> so page registration runs before the bar reads actions. */}
      <BottomNavBar />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {can("ai.copilot") && moduleEnabled("ai_copilot_bo") && (
        <CopilotWidget open={copilotOpen} onOpenChange={setCopilotOpen} />
      )}
      <Toasts />
      <SessionLockOverlay />
    </div>
  );
}
